/**
 * MainBarTile — quick-260626-cot Task 3.3.
 *
 * Analytical replacement for the remaining table tiles. Two variants:
 *
 *   'horizontalBar' — one bar per category, sorted desc. By-channel metrics:
 *       MAIN-34 Lead→Opp % · MAIN-35 Win Rate · MAIN-36 Sales Cycle (days) ·
 *       MAIN-42 Cost per Lead · MAIN-43 Cost per Opp.
 *       Bucket shape: { label: channel, value: N }.
 *
 *   'progress' — per-AE progress-to-target bar (actual vs quota) with a target
 *       tick at 100%. MAIN-44 AE Actual vs Quota.
 *       Bucket shape: { label: AE, actual, quota, pct }.
 *
 * Reuses the existing MAIN-34/35/36/42/43/44 grouped payloads verbatim — no
 * data/recompute change. UX-07 4-state machine mirrors the other Main tiles.
 */
import ReactECharts from 'echarts-for-react';

import {
  CacheNotReadyError,
  SalesforceUnavailableError,
} from '../../lib/api-client.js';
import {
  buildEchartsOption,
  type BarValueUnit,
} from '../../lib/echarts-builder.js';
import { formatAmount } from '../../lib/format.js';
import { SfUnreachableBanner } from '../SfUnreachableBanner.js';
import { Card, CardHeader, CardTitle } from '../ui/Card.js';
import { Skeleton } from '../ui/Skeleton.js';
import { Tooltip } from '../ui/Tooltip.js';

import type { MainGroupedResponse } from '@saledash/shared';

export type BarVariant = 'horizontalBar' | 'progress';

interface MainBarTileProps {
  metricId: string;
  title: string;
  variant: BarVariant;
  /** Value unit for the horizontalBar variant (percent / days / currency). */
  valueUnit?: BarValueUnit;
  tooltipFormula?: string | undefined;
  data: MainGroupedResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
}

/** Per-AE progress bucket parsed off the MAIN-44 payload. */
interface QuotaBucket {
  label: string;
  actual: number;
  quota: number;
  pct: number;
}

function toQuotaBuckets(buckets: MainGroupedResponse['buckets']): QuotaBucket[] {
  return buckets.map((b) => ({
    label: typeof b.label === 'string' ? b.label : '',
    actual: typeof b.actual === 'number' ? b.actual : 0,
    quota: typeof b.quota === 'number' ? b.quota : 0,
    pct:
      typeof b.pct === 'number'
        ? b.pct
        : typeof b.actual === 'number' && typeof b.quota === 'number' && b.quota > 0
          ? b.actual / b.quota
          : 0,
  }));
}

/** Channel bucket { label, value } sorted desc by value. */
function toSortedChannelBuckets(
  buckets: MainGroupedResponse['buckets'],
): { label: string; value: number }[] {
  return buckets
    .map((b) => ({
      label: typeof b.label === 'string' ? b.label : '',
      value: typeof b.value === 'number' ? b.value : 0,
    }))
    .sort((a, b) => b.value - a.value);
}

function formatPct(ratio: number): string {
  return `${String(Math.round(ratio * 100))}%`;
}

export function MainBarTile({
  metricId,
  title,
  variant,
  valueUnit = 'number',
  tooltipFormula,
  data,
  isLoading,
  isError,
  error,
  refetch,
}: MainBarTileProps): React.JSX.Element {
  const tooltipContent = tooltipFormula ? (
    <>
      <p className="mb-1 font-semibold text-ink-900">{title}</p>
      <p>{tooltipFormula}</p>
    </>
  ) : (
    <p className="font-semibold text-ink-900">{title}</p>
  );

  const header = (
    <CardHeader>
      <CardTitle className="text-[0.72rem] uppercase tracking-wider font-semibold text-ink-600">
        {title}
      </CardTitle>
      {tooltipFormula && (
        <Tooltip trigger="?" label="Show definition">
          {tooltipContent}
        </Tooltip>
      )}
    </CardHeader>
  );

  // UX-07: cache-warming
  if (isError && error instanceof CacheNotReadyError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4">
          <div
            role="status"
            className="flex items-start gap-3 rounded-lg border border-warn bg-warn-bg px-4 py-3 text-sm text-warn-text"
            data-testid="cache-warming-banner"
          >
            <span
              aria-hidden
              className="mt-0.5 inline-flex h-5 w-5 flex-none items-center justify-center rounded-full bg-warn text-xs font-bold text-white"
            >
              ⌛
            </span>
            <div className="flex-1">
              <p className="font-semibold">Cache warming up</p>
              <p className="mt-1 text-xs text-warn-text/80">
                First data refresh expected within ~7 minutes.
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  // UX-07: SF-unreachable
  if (isError && error instanceof SalesforceUnavailableError) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4">
          <SfUnreachableBanner onRetry={refetch} />
        </div>
      </Card>
    );
  }

  // Loading
  if (isLoading || !data) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="p-4" aria-busy="true">
          <Skeleton className="h-40 w-full" data-testid="skeleton-bars" />
        </div>
      </Card>
    );
  }

  // Data-driven placeholder (e.g. MAIN-44 "no quotas defined").
  if (data.placeholder_text) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <p className="text-sm text-ink-700" data-testid="bar-placeholder-text">
          {data.placeholder_text}
        </p>
      </Card>
    );
  }

  const buckets = data.buckets;

  if (buckets.length === 0) {
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <p
          className="py-10 text-center text-sm text-ink-500"
          data-testid="bar-empty-state"
        >
          No data
        </p>
      </Card>
    );
  }

  // ── Progress-to-target variant (MAIN-44 AE Actual vs Quota) ──────────────
  if (variant === 'progress') {
    const aes = toQuotaBuckets(buckets).sort((a, b) => b.pct - a.pct);
    return (
      <Card data-testid={`main-tile-${metricId}`}>
        {header}
        <div className="mt-1 space-y-3.5" data-testid="progress-bars">
          {aes.map((ae) => {
            const fillPct = Math.max(0, Math.min(100, Math.round(ae.pct * 100)));
            const atTarget = ae.pct >= 1;
            return (
              <div key={ae.label} data-testid="progress-bar-row">
                <div className="mb-1 flex items-baseline justify-between text-xs">
                  <span className="font-medium text-ink-700">{ae.label}</span>
                  <span className="tabular-nums text-ink-500">
                    {formatAmount(ae.actual)}
                    <span className="text-ink-400"> / {formatAmount(ae.quota)}</span>
                    <span
                      className={`ml-1.5 font-semibold ${atTarget ? 'text-good-text' : 'text-ink-700'}`}
                    >
                      {formatPct(ae.pct)}
                    </span>
                  </span>
                </div>
                <div className="relative h-[7px] overflow-visible rounded-full bg-ink-100">
                  <div
                    className={`h-[7px] rounded-full ${atTarget ? 'bg-good-text' : 'bg-adeptia-cyan'}`}
                    style={{ width: `${String(fillPct)}%` }}
                  />
                  {/* Target tick at 100%. */}
                  <div
                    aria-hidden
                    className="absolute top-[-3px] h-[13px] w-0.5 rounded-sm bg-ink-700"
                    style={{ left: '100%' }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-[10.5px] text-ink-400">
          Closed-won this quarter vs quota · tick = 100% of target
        </p>
      </Card>
    );
  }

  // ── Horizontal-bar variant (MAIN-34/35/36/42/43 by channel) ──────────────
  const sorted = toSortedChannelBuckets(buckets);
  const chartOption = buildEchartsOption(
    'horizontalBar',
    {
      categories: sorted.map((b) => b.label),
      series: [{ name: title, values: sorted.map((b) => b.value) }],
    },
    undefined,
    { valueUnit },
  );

  // Height scales with the number of bars so each stays ~28px tall and readable.
  const chartHeight = Math.max(140, sorted.length * 34 + 24);

  return (
    <Card data-testid={`main-tile-${metricId}`}>
      {header}
      <div
        className="w-full"
        style={{ height: `${String(chartHeight)}px` }}
        data-testid="horizontal-bar-container"
      >
        <ReactECharts
          option={chartOption}
          style={{ height: '100%', width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge
          lazyUpdate={false}
        />
      </div>
    </Card>
  );
}
